<template>
  <v-data-table
    class="dashboard-data-table"
    :headers="vuetifyHeaders"
    :items="rows"
    :item-key="itemKey"
    :hide-default-footer="hideDefaultFooter"
    :disable-pagination="hideDefaultFooter"
    :disable-sort="disableSort"
    :custom-sort="serverSort ? keepServerOrder : undefined"
    :group-by="effectiveGroupBy"
    :server-items-length="serverItemsLength"
    :options="options"
    :footer-props="{
      'items-per-page-options': [5, 10, 15, 20, 25, 50],
      'items-per-page-text': 'Linhas por página:',
      'page-text': '{0}-{1} de {2}'
    }"
    @update:options="$emit('update:options', $event)"
  >
    <template
      v-for="column in columns"
      v-slot:[`item.${column.key}`]="{ item }"
    >
      <slot
        name="cell"
        :column="column"
        :row="item"
      >
        <BaseDashboardTableCell
          :column="column"
          :row="item"
          :data-field="cellDataField(column, item)"
          :data-row-key="rowKeyOf(item)"
          @detail="$emit('detail', item)"
          @action="$emit('action', $event)"
        />
      </slot>
    </template>

    <template v-slot:group.header="{ group, isOpen, toggle }">
      <td
        :colspan="columns.length"
        class="dashboard-data-table__group-header"
        @click="toggle && toggle()"
      >
        <span class="dashboard-data-table__group-arrow">{{ isOpen ? '▾' : '▸' }}</span>
        {{ group }}
      </td>
    </template>

    <template v-slot:group.summary="{ group }">
      <td
        v-for="column in columns"
        :key="column.key"
        :class="`text-${defaultAlign(column)}`"
        :data-field="sanitizeDataField(column.key)"
      >
        {{ subtotalValue(group, column) }}
      </td>
    </template>
  </v-data-table>
</template>

<script>
import BaseDashboardTableCell from './BaseDashboardTableCell.vue'
import { sanitizeDataField } from './dataFieldUtils'

const ACTION_CELL_TYPES = ['row_action', 'detail_drawer']

export default {
  name: 'BaseDashboardTable',
  components: { BaseDashboardTableCell },
  props: {
    columns: {
      type: Array,
      required: true
    },
    rows: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      required: false,
      default: undefined
    },
    hideDefaultFooter: {
      type: Boolean,
      required: false,
      default: true
    },
    disableSort: {
      type: Boolean,
      required: false,
      default: false
    },
    serverSort: {
      type: Boolean,
      required: false,
      default: true
    },
    groupBy: {
      type: String,
      required: false,
      default: ''
    },
    groupSubtotals: {
      type: Object,
      required: false,
      default: () => ({})
    },
    serverItemsLength: {
      type: Number,
      required: false,
      default: -1
    },
    options: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  computed: {
    vuetifyHeaders() {
      return this.columns.map((column) => ({
        text: column.label,
        value: column.key,
        align: this.defaultAlign(column),
        sortable: !this.disableSort && column.sortable === true
      }))
    },
    effectiveGroupBy() {
      return this.groupBy || null
    }
  },
  methods: {
    rowKeyOf(row) {
      if (!row) {
        return undefined
      }
      if (row._rowKey) {
        return sanitizeDataField(row._rowKey)
      }
      if (!this.itemKey) {
        return undefined
      }
      const value = this.itemKey.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row)
      return sanitizeDataField(value)
    },
    cellDataField(column, row) {
      const rowKey = this.rowKeyOf(row)
      return rowKey ? `${sanitizeDataField(column.key)}-${rowKey}` : sanitizeDataField(column.key)
    },
    subtotalValue(group, column) {
      const subtotal = (group && this.groupSubtotals[group]) || {}
      return subtotal[column.key] || ''
    },
    keepServerOrder(items) {
      if (!this.effectiveGroupBy) {
        return items
      }
      const groups = new Map()
      items.forEach((item) => {
        const key = item[this.effectiveGroupBy]
        if (!groups.has(key)) {
          groups.set(key, [])
        }
        groups.get(key).push(item)
      })
      return [].concat(...groups.values())
    },
    defaultAlign(column) {
      if (column.align) {
        return column.align
      }
      return ACTION_CELL_TYPES.includes(column.cellType) ? 'end' : 'start'
    },
    sanitizeDataField
  }
}
</script>

<style lang="scss">
.dashboard-data-table.v-data-table {
  border: 1px solid var(--ds-color-black-010);
  border-radius: 8px;
  overflow: hidden;
  background: transparent;

  .v-data-table-header {
    background: var(--ds-color-black-005);

    th {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--ds-color-black-055) !important;
      font-weight: 700;
    }
    th.sortable {
      padding-right: 28px;

      .v-data-table-header__icon {
        vertical-align: middle;
        margin-left: 4px;
        margin-right: -22px;
      }

      &.text-end {
        padding-right: 16px;
        padding-left: 28px;

        .v-data-table-header__icon {
          margin-left: -22px;
          margin-right: 4px;
        }
      }
    }
  }

  td {
    font-size: 0.875rem;
    height: auto !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    color: var(--v-textPrimary-base);
    white-space: nowrap;
  }

  .v-row-group__header {
    background: var(--ds-color-black-005);
  }

  .v-row-group__summary {
    background: var(--v-primaryFaint-base);

    td {
      font-weight: 700;
    }
  }

  .dashboard-data-table__group-header {
    cursor: pointer;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--ds-color-black-055);
    font-weight: 700;
  }

  .dashboard-data-table__group-arrow {
    display: inline-block;
    width: 14px;
    color: var(--ds-color-black-040);
  }
}
</style>
